import React, { useMemo } from 'react';

import Form from '@hero/ui-kit/inputs/Form';
import SearchBar from './SearchBar';
import { UseSearchSuggestions } from './types';
import useSearchController, { SEARCH_PARAM } from './useSearchController';
import getSearchSchema from './validators';
import * as Style from './style.module.scss';

export { SEARCH_PARAM };

export type SearchProps = {
    searchLabel?: string;
    removeQueryParamsOnSearch?: string[];
    // Optional per-page hook that feeds quick-result suggestions into the dropdown.
    useSearchSuggestions?: UseSearchSuggestions;
    // Optional per-page filter sections, rendered inside a popover anchored to a
    // funnel badge in the search field. Each page composes its own filters.
    filters?: React.ReactNode;
    // The URL query keys that count as active filters — drives the badge count.
    filterParams?: string[];
    // Turns the field into a jump-to control instead of a list filter: the term
    // never lands in the current page's URL, and submitting (Enter without a
    // highlighted suggestion, or "Show all results") routes to this path with the
    // term applied as `?search=`. Used on detail pages, which have no list to filter.
    searchTo?: string;
};

const SearchForm: React.FC<SearchProps> = ({
    searchLabel,
    removeQueryParamsOnSearch = ['limit', 'offset'], // by default remove pagination
    useSearchSuggestions,
    filters,
    filterParams,
    searchTo
}) => {
    const { syncedSearch, pathname, submit, setTopSuggestion, reset } = useSearchController({
        removeQueryParamsOnSearch,
        filterParams,
        searchTo
    });

    const schema = useMemo(() => getSearchSchema(), []);
    const defaultValues = useMemo(() => ({ [SEARCH_PARAM]: syncedSearch }), [syncedSearch]);

    return (
        <div className={Style.searchWrapper}>
            <Form validationSchema={schema} defaultValues={defaultValues} submitFn={submit}>
                <SearchBar
                    name={SEARCH_PARAM}
                    searchLabel={searchLabel}
                    syncedSearch={syncedSearch}
                    pathname={pathname}
                    onSubmit={submit}
                    onTopSuggestionChange={setTopSuggestion}
                    onClear={reset}
                    useSearchSuggestions={useSearchSuggestions}
                    filters={filters}
                    filterParams={filterParams}
                />
            </Form>
        </div>
    );
};

export default SearchForm;
